import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ADMIN_TOKEN_KEY,
  api,
  clearParticipantAuth,
  clearParticipantSession,
  getApiErrorMessage,
  loadParticipantAccount,
  loadParticipantSession,
  saveParticipantAccount,
  saveParticipantAuth,
  saveParticipantSession,
} from './api-v2';
import './AppV2.css';

const EXPERIMENTS = {
  E1: {
    number: '01',
    name: '沟通意图理解任务',
    description: '阅读真实沟通材料，并回答三项意图理解问题。',
  },
  E2: {
    number: '02',
    name: '沟通回复任务',
    description: '阅读真实沟通材料，并形成一份可以直接发送的回复。',
  },
};

const EXPORTS = [
  ['participants', '参与者'],
  ['experiment1_answers', '实验一回答'],
  ['experiment2_replies', '实验二回复'],
  ['task_surveys', '任务后问卷'],
  ['final_surveys', '最终问卷'],
  ['comprehension_checks', '理解检验'],
  ['task_records', '任务记录'],
  ['behavior_logs', '行为日志'],
];

function experimentFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const query = params.get('experiment');
  if (query === '1' || window.location.pathname.includes('/experiment/1')) return 'E1';
  if (query === '2' || window.location.pathname.includes('/experiment/2')) return 'E2';
  return null;
}

function useCountdown(unlockAt) {
  const calculate = () => {
    if (!unlockAt) return 0;
    return Math.max(0, Math.ceil((new Date(unlockAt).getTime() - Date.now()) / 1000));
  };
  const [remaining, setRemaining] = useState(calculate);
  useEffect(() => {
    setRemaining(calculate());
    if (!unlockAt) return undefined;
    const timer = window.setInterval(() => setRemaining(calculate()), 250);
    return () => window.clearInterval(timer);
  }, [unlockAt]);
  return remaining;
}

function LoadingScreen({ label = '正在载入实验…' }) {
  return (
    <main className="center-screen">
      <div className="loading-mark" aria-hidden="true" />
      <p>{label}</p>
    </main>
  );
}

function ParticipantAuth({ onAuthenticated }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ username: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function switchMode(nextMode) {
    setMode(nextMode);
    setError('');
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (mode === 'register' && form.password !== form.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    setBusy(true);
    try {
      const credentials = { username: form.username, password: form.password };
      const response = mode === 'register'
        ? await api.register(credentials)
        : await api.login(credentials);
      saveParticipantAuth(response);
      onAuthenticated(response.account);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="participant-auth">
      <section className="auth-intro">
        <span className="brand-mark" aria-hidden="true">D</span>
        <p className="eyebrow">COMMUNICATION RESEARCH</p>
        <h1>AI辅助沟通实验</h1>
        <p>登录后可开始实验或继续上次进度。每个账号只能参加其中一项实验。</p>
        <a href="/admin">管理员入口</a>
      </section>
      <section className="auth-panel">
        <div className="auth-tabs" role="tablist" aria-label="账号操作">
          <button className={mode === 'login' ? 'active' : ''} type="button" onClick={() => switchMode('login')}>登录</button>
          <button className={mode === 'register' ? 'active' : ''} type="button" onClick={() => switchMode('register')}>注册</button>
        </div>
        <form onSubmit={submit}>
          <div>
            <p className="eyebrow">{mode === 'login' ? 'WELCOME BACK' : 'CREATE ACCOUNT'}</p>
            <h2>{mode === 'login' ? '参与者登录' : '注册参与者账号'}</h2>
            <p>{mode === 'login' ? '使用注册时填写的账号和密码登录。' : '账号用于保存实验进度，请妥善保管密码。'}</p>
          </div>
          <label>
            <span>账号</span>
            <input
              autoComplete="username"
              value={form.username}
              onChange={(event) => setForm({ ...form, username: event.target.value })}
              placeholder="3至32位字符"
              required
            />
          </label>
          <label>
            <span>密码</span>
            <input
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              minLength="8"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              placeholder="至少8位"
              required
            />
          </label>
          {mode === 'register' && (
            <label>
              <span>确认密码</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength="8"
                value={form.confirmPassword}
                onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })}
                required
              />
            </label>
          )}
          {error && <p className="error-banner">{error}</p>}
          <button className="button button-primary button-wide" type="submit" disabled={busy}>
            {busy ? '正在提交…' : mode === 'login' ? '登录并继续' : '注册并进入'}
          </button>
        </form>
      </section>
    </main>
  );
}

function Header({ experimentId, state, account, onExit, onLogout }) {
  const progress = state?.totalTasks
    ? Math.round((state.completedTasks / state.totalTasks) * 100)
    : 0;
  return (
    <header className="topbar">
      <div className="brand-lockup">
        <span className="brand-mark" aria-hidden="true">D</span>
        <div>
          <strong>AI辅助沟通实验</strong>
          <span>{experimentId ? EXPERIMENTS[experimentId].name : '研究平台'}</span>
        </div>
      </div>
      {state?.task && (
        <div className="progress-wrap" aria-label={`实验进度 ${progress}%`}>
          <div className="progress-copy">
            <span>实验进度</span>
            <strong>{state.completedTasks + 1} / {state.totalTasks}</strong>
          </div>
          <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
        </div>
      )}
      <div className="header-actions">
        {account && <span>{account.username}</span>}
        {onExit && <button className="button button-quiet" type="button" onClick={onExit}>返回首页</button>}
        {onLogout && <button className="button button-quiet" type="button" onClick={onLogout}>退出登录</button>}
      </div>
    </header>
  );
}

function EntryChooser({ account, onLogout }) {
  const session = loadParticipantSession();
  return (
    <div className="app-shell entry-shell">
      <Header account={account} onLogout={onLogout} />
      <main className="entry-main">
        <section className="entry-intro">
          <p className="eyebrow">COMMUNICATION RESEARCH</p>
          <h1>AI辅助沟通实验</h1>
          <p>请选择研究人员提供给你的实验入口。每位参与者只需完成指定的一项实验。</p>
        </section>
        {session?.experimentId ? (
          <div className="resume-strip">
            <div>
              <strong>{session.status === 'completed' ? '你已完成本次实验' : '继续已开始的实验'}</strong>
              <span>参与者编号：{session.participantCode} · {EXPERIMENTS[session.experimentId].name}</span>
            </div>
            <a className="button button-primary" href={`/experiment/${session.experimentId === 'E1' ? '1' : '2'}`}>
              {session.status === 'completed' ? '查看完成码' : '继续实验'}
            </a>
          </div>
        ) : (
          <section className="experiment-grid" aria-label="实验入口">
            {Object.entries(EXPERIMENTS).map(([id, item]) => (
              <a className="experiment-option" href={`/experiment/${id === 'E1' ? '1' : '2'}`} key={id}>
                <span className="experiment-number">{item.number}</span>
                <div>
                  <h2>{item.name}</h2>
                  <p>{item.description}</p>
                  <span className="text-link">进入实验 <span aria-hidden="true">→</span></span>
                </div>
              </a>
            ))}
          </section>
        )}
        <a className="admin-entry" href="/admin">管理员入口</a>
      </main>
    </div>
  );
}

function ConsentForm({ experimentId, account, onStarted }) {
  const [form, setForm] = useState({
    age: '',
    gender: '',
    education: '',
    aiExperience: '',
    consentAccepted: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  async function submit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      clearParticipantSession();
      const payload = await api.start({ ...form, experimentId });
      saveParticipantSession(payload.session);
      onStarted(payload.session);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <Header experimentId={experimentId} account={account} onExit={() => { window.location.href = '/'; }} />
      <main className="form-page">
        <section className="form-heading">
          <p className="eyebrow">实验 {EXPERIMENTS[experimentId].number}</p>
          <h1>{EXPERIMENTS[experimentId].name}</h1>
          <p>实验约包含 5 个独立任务。请在安静环境中完成，过程中不要刷新或关闭页面。</p>
        </section>
        <form className="research-form" onSubmit={submit}>
          <fieldset>
            <legend>参与说明</legend>
            <div className="consent-text">
              <p>本研究关注人们在工作沟通场景中的理解与回复过程。你的回答仅用于研究分析，系统会使用随机编号保存数据。</p>
              <p>参与完全自愿。你可以在提交前退出；正式开始后请独立完成，不使用页面之外的AI工具。</p>
            </div>
            <label className="check-row">
              <input
                type="checkbox"
                checked={form.consentAccepted}
                onChange={(event) => update('consentAccepted', event.target.checked)}
              />
              <span>我已阅读以上说明，自愿参加本实验，且已年满18周岁。</span>
            </label>
          </fieldset>
          <fieldset>
            <legend>基本信息</legend>
            <div className="field-grid">
              <label>
                <span>年龄</span>
                <input
                  type="number"
                  min="18"
                  max="100"
                  value={form.age}
                  onChange={(event) => update('age', event.target.value)}
                  placeholder="请输入"
                  required
                />
              </label>
              <label>
                <span>性别</span>
                <select value={form.gender} onChange={(event) => update('gender', event.target.value)} required>
                  <option value="">请选择</option>
                  <option value="female">女</option>
                  <option value="male">男</option>
                  <option value="other">其他 / 不便透露</option>
                </select>
              </label>
              <label>
                <span>最高学历</span>
                <select value={form.education} onChange={(event) => update('education', event.target.value)} required>
                  <option value="">请选择</option>
                  <option value="high-school">高中及以下</option>
                  <option value="college">大专</option>
                  <option value="bachelor">本科</option>
                  <option value="master">硕士</option>
                  <option value="doctor">博士</option>
                </select>
              </label>
              <label>
                <span>生成式AI使用经验</span>
                <select value={form.aiExperience} onChange={(event) => update('aiExperience', event.target.value)} required>
                  <option value="">请选择</option>
                  <option value="never">从未使用</option>
                  <option value="rarely">偶尔使用</option>
                  <option value="weekly">每周使用</option>
                  <option value="daily">几乎每天使用</option>
                </select>
              </label>
            </div>
          </fieldset>
          {error && <p className="error-banner" role="alert">{error}</p>}
          <button className="button button-primary button-wide" type="submit" disabled={busy || !form.consentAccepted}>
            {busy ? '正在创建实验…' : '开始实验'}
          </button>
        </form>
      </main>
    </div>
  );
}

function MaterialPanel({ task }) {
  return (
    <section className="material-panel">
      <div className="section-kicker">任务材料</div>
      <h1>{task.title}</h1>
      <p className="background-copy">{task.background}</p>
      {task.terminology?.length > 0 && (
        <div className="term-list">
          {task.terminology.map((item) => (
            <div className="term-row" key={item.term}>
              <strong>{item.term}</strong>
              <span>{item.explanation}</span>
            </div>
          ))}
        </div>
      )}
      <div className="dialogue-list">
        {task.dialogues.map((line) => (
          <div className="dialogue-row" key={`${line.order}-${line.speaker}`}>
            <span>{line.speaker}</span>
            <p>{line.content}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function AiReveal({ task, onState, setError }) {
  const remaining = useCountdown(task.firstUnlockAt);
  const [busy, setBusy] = useState(false);
  async function reveal() {
    setBusy(true);
    setError('');
    try {
      const result = await api.clickAi(task.taskId);
      onState(result.state);
    } catch (error) {
      setError(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  if (task.aiClicked) return null;
  return (
    <div className="ai-gate">
      <div>
        <strong>{remaining > 0 ? `请继续阅读材料（${remaining}秒）` : '材料阅读完成'}</strong>
        <span>阅读完成后，由你主动决定何时查看AI辅助内容。</span>
      </div>
      <button className="button button-primary" type="button" disabled={remaining > 0 || busy} onClick={reveal}>
        {busy ? '正在载入…' : '查看AI辅助'}
      </button>
    </div>
  );
}

function ComprehensionGate({ task, onState, setError }) {
  const remaining = useCountdown(task.firstUnlockAt);
  const [selected, setSelected] = useState('');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  if (task.comprehension?.passed) return <p className="success-note">材料理解检验已通过。</p>;

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const response = await api.answerComprehension(task.taskId, Number(selected));
      if (response.result.correct) {
        setFeedback('回答正确，可以继续。');
        const refreshed = await api.state();
        onState(refreshed.state);
      } else {
        setFeedback('回答不正确，请重新阅读材料后再选择。');
      }
    } catch (error) {
      setError(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="comprehension-panel">
      <div className="section-kicker">材料理解检验</div>
      <h2>{task.comprehension.question}</h2>
      <div className="option-list">
        {task.comprehension.options.map((option, index) => (
          <label className={`option-row ${String(index) === selected ? 'selected' : ''}`} key={option}>
            <input
              type="radio"
              name="comprehension"
              value={index}
              checked={String(index) === selected}
              onChange={(event) => setSelected(event.target.value)}
              disabled={remaining > 0}
            />
            <span className="option-letter">{String.fromCharCode(65 + index)}</span>
            <span>{option}</span>
          </label>
        ))}
      </div>
      {feedback && <p className={feedback.includes('正确') ? 'success-note' : 'warning-note'}>{feedback}</p>}
      <button
        className="button button-secondary"
        type="button"
        disabled={remaining > 0 || selected === '' || busy}
        onClick={submit}
      >
        {remaining > 0 ? `${remaining}秒后可作答` : busy ? '正在提交…' : '提交检验'}
      </button>
    </section>
  );
}

function E1Response({ task, onState, setError }) {
  const [answers, setAnswers] = useState(() => Object.fromEntries(task.questions.map((item) => [item.id, ''])));
  const [busy, setBusy] = useState(false);
  const startTime = useRef(Date.now());
  const ready = task.questions.every((item) => answers[item.id]?.trim());

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.saveResponse({
        taskId: task.taskId,
        durationMs: Date.now() - startTime.current,
        answers: task.questions.map((item) => ({
          questionType: item.id,
          answerText: answers[item.id],
        })),
      });
      onState(result.state);
    } catch (error) {
      setError(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="response-form" onSubmit={submit}>
      <div className="section-kicker">你的判断</div>
      {task.questions.map((question, index) => (
        <label className="long-field" key={question.id}>
          <span><b>{String(index + 1).padStart(2, '0')}</b>{question.text}</span>
          <textarea
            rows="4"
            value={answers[question.id]}
            onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
            placeholder="请结合材料具体说明"
            maxLength="5000"
          />
        </label>
      ))}
      <button className="button button-primary" type="submit" disabled={!ready || busy}>
        {busy ? '正在提交…' : '提交本题回答'}
      </button>
    </form>
  );
}

function GeneratedReply({ task, onState, setError }) {
  const remaining = useCountdown(task.candidateUnlockAt);
  const [replyText, setReplyText] = useState('');
  const [copiedId, setCopiedId] = useState(task.selectedCandidateId || null);
  const [busy, setBusy] = useState(false);
  const startTime = useRef(Date.now());

  async function copyCandidate(candidateId) {
    setError('');
    try {
      const response = await api.copyCandidate(task.taskId, candidateId);
      await navigator.clipboard.writeText(response.result.text);
      setCopiedId(candidateId);
      setReplyText('');
    } catch (error) {
      setError(getApiErrorMessage(error, '复制失败，请允许浏览器访问剪贴板后重试。'));
    }
  }

  function pasteOnly(event) {
    event.preventDefault();
    setReplyText(event.clipboardData.getData('text'));
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.saveResponse({
        taskId: task.taskId,
        replyText,
        durationMs: Date.now() - startTime.current,
      });
      onState(result.state);
    } catch (error) {
      setError(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="response-form generated-form" onSubmit={submit}>
      <div className="section-kicker">AI推荐回复</div>
      <p className="instruction-copy">阅读三份回复，选择其中一份。倒计时结束后点击“复制此回复”，再将完整内容粘贴到提交框。</p>
      <div className="candidate-list">
        {task.aiContent.replies.map((candidate) => (
          <article className={`candidate ${copiedId === candidate.candidateId ? 'chosen' : ''}`} key={candidate.candidateId}>
            <div className="candidate-heading">
              <strong>推荐回复 {candidate.displayPosition}</strong>
              <button
                className="button button-small"
                type="button"
                disabled={remaining > 0}
                onClick={() => copyCandidate(candidate.candidateId)}
              >
                {copiedId === candidate.candidateId ? '已复制' : remaining > 0 ? `${remaining}秒` : '复制此回复'}
              </button>
            </div>
            <p>{candidate.text}</p>
          </article>
        ))}
      </div>
      <label className="long-field paste-field">
        <span>最终回复</span>
        <textarea
          rows="7"
          value={replyText}
          onPaste={pasteOnly}
          onChange={() => {}}
          onKeyDown={(event) => {
            const pasteShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v';
            if (!pasteShortcut && event.key !== 'Tab') event.preventDefault();
          }}
          placeholder={copiedId ? '请在此处粘贴刚刚复制的完整回复' : '请先选择并复制一份推荐回复'}
          disabled={!copiedId}
        />
      </label>
      <button className="button button-primary" type="submit" disabled={!copiedId || !replyText.trim() || busy}>
        {busy ? '正在提交…' : '提交最终回复'}
      </button>
    </form>
  );
}

function E2Response({ task, onState, setError }) {
  const [replyText, setReplyText] = useState('');
  const [busy, setBusy] = useState(false);
  const startTime = useRef(Date.now());

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.saveResponse({
        taskId: task.taskId,
        replyText,
        durationMs: Date.now() - startTime.current,
      });
      onState(result.state);
    } catch (error) {
      setError(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="response-form" onSubmit={submit}>
      <div className="section-kicker">你的回复</div>
      <label className="long-field">
        <span>请写出一份可以直接发送给客户的完整回复</span>
        <textarea
          rows="8"
          value={replyText}
          onChange={(event) => setReplyText(event.target.value)}
          placeholder="请输入回复内容"
          maxLength="5000"
        />
      </label>
      <button className="button button-primary" type="submit" disabled={!replyText.trim() || busy}>
        {busy ? '正在提交…' : '提交最终回复'}
      </button>
    </form>
  );
}

function TaskSurvey({ task, onState, setError }) {
  const [scores, setScores] = useState({});
  const [busy, setBusy] = useState(false);
  const ready = task.surveyItems.every((item) => scores[item.itemId]);
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.saveSurvey({
        taskId: task.taskId,
        phase: 'task_post',
        answers: task.surveyItems.map((item) => ({ itemId: item.itemId, score: scores[item.itemId] })),
      });
      onState(result.state);
    } catch (error) {
      setError(getApiErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="survey-panel">
      <div className="section-kicker">本题体验评价</div>
      <h2>请根据刚才的实际体验作答</h2>
      <p className="scale-legend"><span>1 = 完全不同意</span><span>7 = 完全同意</span></p>
      <form onSubmit={submit}>
        {task.surveyItems.map((item, index) => (
          <div className="survey-item" key={item.itemId}>
            <p><b>{String(index + 1).padStart(2, '0')}</b>{item.text}</p>
            <div className="score-row" role="radiogroup" aria-label={item.text}>
              {[1, 2, 3, 4, 5, 6, 7].map((score) => (
                <label className={scores[item.itemId] === score ? 'selected' : ''} key={score}>
                  <input
                    type="radio"
                    name={item.itemId}
                    value={score}
                    checked={scores[item.itemId] === score}
                    onChange={() => setScores((current) => ({ ...current, [item.itemId]: score }))}
                  />
                  <span>{score}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
        <button className="button button-primary" type="submit" disabled={!ready || busy}>
          {busy ? '正在保存…' : '保存并进入下一题'}
        </button>
      </form>
    </section>
  );
}

function TaskWorkspace({ state, onState }) {
  const task = state.task;
  const [error, setError] = useState('');
  const needsAi = task.mode !== 'human';
  const canAnswer = task.mode === 'human' || task.aiClicked;
  const comprehensionReady = task.mode !== 'generated' || task.comprehension?.passed;

  if (task.status === 'response_submitted') {
    return (
      <main className="task-page survey-page">
        <aside className="task-context">
          <span>已完成任务 {task.order}</span>
          <strong>{task.title}</strong>
        </aside>
        <TaskSurvey task={task} onState={onState} setError={setError} />
        {error && <p className="error-banner floating-error">{error}</p>}
      </main>
    );
  }

  return (
    <main className="task-page">
      <MaterialPanel task={task} />
      <section className="work-panel">
        <div className="work-heading">
          <span>任务 {String(task.order).padStart(2, '0')}</span>
          <strong>{state.experimentId === 'E1' ? '判断沟通意图' : '形成沟通回复'}</strong>
        </div>
        {task.mode === 'generated' && !task.comprehension?.passed && (
          <ComprehensionGate task={task} onState={onState} setError={setError} />
        )}
        {needsAi && comprehensionReady && !task.aiClicked && (
          <AiReveal task={task} onState={onState} setError={setError} />
        )}
        {task.aiClicked && task.aiContent?.analysis && (
          <section className="ai-output">
            <div className="section-kicker">AI分析辅助</div>
            <p>{task.aiContent.analysis}</p>
          </section>
        )}
        {task.aiClicked && task.aiContent?.guidance && (
          <section className="ai-output">
            <div className="section-kicker">AI回复指导</div>
            <p>{task.aiContent.guidance}</p>
          </section>
        )}
        {error && <p className="error-banner" role="alert">{error}</p>}
        {canAnswer && state.experimentId === 'E1' && (
          <E1Response task={task} onState={onState} setError={setError} />
        )}
        {canAnswer && state.experimentId === 'E2' && task.mode !== 'generated' && (
          <E2Response task={task} onState={onState} setError={setError} />
        )}
        {canAnswer && state.experimentId === 'E2' && task.mode === 'generated' && (
          <GeneratedReply task={task} onState={onState} setError={setError} />
        )}
      </section>
    </main>
  );
}

function Completion({ state, onState }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function complete() {
    setBusy(true);
    setError('');
    try {
      const response = await api.complete();
      onState(response.state);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }
  const completed = state.status === 'completed';
  return (
    <main className="completion-page">
      <div className="completion-mark" aria-hidden="true">{completed ? '✓' : '5/5'}</div>
      <p className="eyebrow">{completed ? 'EXPERIMENT COMPLETED' : 'ALL TASKS COMPLETED'}</p>
      <h1>{completed ? '感谢你的参与' : '所有任务均已完成'}</h1>
      <p>{completed ? '你的回答已经成功保存。请记录下方完成码，并按研究人员要求返回。' : '请点击下方按钮确认提交本次实验。提交后答案将不能修改。'}</p>
      {completed ? (
        <div className="completion-code">
          <span>实验完成码</span>
          <strong data-copy-allowed="true">{state.completionCode}</strong>
        </div>
      ) : (
        <button className="button button-primary" type="button" onClick={complete} disabled={busy}>
          {busy ? '正在完成…' : '确认完成实验'}
        </button>
      )}
      {error && <p className="error-banner">{error}</p>}
    </main>
  );
}

function ParticipantApp({ experimentId, account }) {
  const [session, setSession] = useState(() => loadParticipantSession());
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(Boolean(session?.experimentId === experimentId));
  const [error, setError] = useState('');

  useEffect(() => {
    function blockPageCopy(event) {
      if (!event.target.closest('input, textarea, [data-copy-allowed="true"]')) event.preventDefault();
    }
    document.addEventListener('copy', blockPageCopy);
    return () => document.removeEventListener('copy', blockPageCopy);
  }, []);

  useEffect(() => {
    if (!session || session.experimentId !== experimentId) {
      setLoading(false);
      return;
    }
    let active = true;
    api.state()
      .then((response) => {
        if (active) setState(response.state);
      })
      .catch((requestError) => {
        if (active) setError(getApiErrorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [experimentId, session]);

  function exit() {
    if (!window.confirm('确定退出本次实验吗？你的已提交数据会保留。')) return;
    window.location.href = '/';
  }

  if (loading) return <LoadingScreen />;
  if (session && session.experimentId !== experimentId) {
    return (
      <div className="app-shell">
        <Header account={account} onExit={() => { window.location.href = '/'; }} />
        <main className="center-screen">
          <p className="eyebrow">EXPERIMENT ALREADY SELECTED</p>
          <h1>此账号已绑定另一项实验</h1>
          <p>每个参与者账号只能参加一项实验，请返回首页继续原实验。</p>
          <a className="button button-primary" href="/">返回首页</a>
        </main>
      </div>
    );
  }
  if (!session) {
    return (
      <ConsentForm
        experimentId={experimentId}
        account={account}
        onStarted={(nextSession) => setSession(nextSession)}
      />
    );
  }
  if (error && !state) {
    return (
      <main className="center-screen">
        <p className="error-banner">{error}</p>
        <button className="button button-secondary" type="button" onClick={() => window.location.reload()}>重新连接</button>
      </main>
    );
  }
  if (!state) return <LoadingScreen />;
  return (
    <div className="app-shell">
      <Header experimentId={experimentId} state={state} account={account} onExit={exit} />
      {state.task
        ? <TaskWorkspace key={state.task.taskId} state={state} onState={setState} />
        : <Completion state={state} onState={setState} />}
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [form, setForm] = useState({ username: 'admin', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await api.adminLogin(form);
      localStorage.setItem(ADMIN_TOKEN_KEY, response.token);
      onLogin();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="admin-login">
      <a className="back-link" href="/">← 返回实验入口</a>
      <form onSubmit={submit}>
        <span className="brand-mark">D</span>
        <h1>研究管理后台</h1>
        <p>登录后可查看实验进度、内容状态并导出研究数据。</p>
        <label>
          <span>管理员账号</span>
          <input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} required />
        </label>
        <label>
          <span>密码</span>
          <input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required />
        </label>
        {error && <p className="error-banner">{error}</p>}
        <button className="button button-primary button-wide" type="submit" disabled={busy}>
          {busy ? '正在登录…' : '登录'}
        </button>
      </form>
    </main>
  );
}

function SettingField({ label, hint, children }) {
  return (
    <div className="setting-row">
      <div><strong>{label}</strong><span>{hint}</span></div>
      {children}
    </div>
  );
}

function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(Boolean(localStorage.getItem(ADMIN_TOKEN_KEY)));
  const [summary, setSummary] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [settings, setSettings] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const completionRate = summary?.totalParticipants
    ? Math.round((summary.completedParticipants / summary.totalParticipants) * 100)
    : 0;

  async function load() {
    setError('');
    try {
      const [summaryResponse, participantResponse, settingsResponse] = await Promise.all([
        api.adminSummary(),
        api.adminParticipants(),
        api.adminSettings(),
      ]);
      setSummary(summaryResponse.summary);
      setParticipants(participantResponse.participants);
      setSettings(settingsResponse.settings);
    } catch (requestError) {
      if (requestError.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthenticated(false);
      } else {
        setError(getApiErrorMessage(requestError));
      }
    }
  }

  useEffect(() => {
    if (authenticated) load();
  }, [authenticated]);

  async function saveSettings() {
    setError('');
    try {
      const response = await api.updateAdminSettings(settings);
      setSettings(response.settings);
      setNotice('设置已保存，新开始的参与者将使用最新设置。');
      window.setTimeout(() => setNotice(''), 3500);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    }
  }

  async function download(name) {
    try {
      const blob = await api.downloadExport(name);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${name}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    }
  }

  if (!authenticated) return <AdminLogin onLogin={() => setAuthenticated(true)} />;
  if (!summary || !settings) return <LoadingScreen label="正在载入管理后台…" />;

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <a className="brand-lockup admin-brand" href="/admin">
          <span className="brand-mark">D</span>
          <div><strong>研究控制台</strong><span>{summary.studyId}</span></div>
        </a>
        <nav>
          <a className="active" href="#overview">实验概览</a>
          <a href="#groups">分组进度</a>
          <a href="#participants">参与者</a>
          <a href="#exports">数据导出</a>
          <a href="#settings">实验设置</a>
        </nav>
        <button
          className="button button-quiet"
          type="button"
          onClick={() => {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            setAuthenticated(false);
          }}
        >
          退出管理后台
        </button>
      </aside>
      <main className="admin-main">
        <header className="admin-heading" id="overview">
          <div><p className="eyebrow">RESEARCH OPERATIONS</p><h1>实验运行概览</h1></div>
          <button className="button button-secondary" type="button" onClick={load}>刷新数据</button>
        </header>
        {error && <p className="error-banner">{error}</p>}
        {notice && <p className="success-banner">{notice}</p>}
        <section className="metric-grid">
          <article><span>参与者总数</span><strong>{summary.totalParticipants}</strong><small>两个实验合计</small></article>
          <article><span>已完成</span><strong>{summary.completedParticipants}</strong><small>完成率 {completionRate}%</small></article>
          <article><span>正式案例</span><strong>{summary.content.taskCount}</strong><small>实验一 7 / 实验二 8</small></article>
          <article><span>数据源</span><strong>3</strong><small>当前Markdown材料</small></article>
        </section>
        <section className="admin-section" id="groups">
          <div className="admin-section-heading"><div><h2>六组分配情况</h2><p>两个实验分别进行 1:1:1 平衡分配。</p></div></div>
          <div className="group-table">
            {summary.groups.map((group) => (
              <div className="group-row" key={group.groupId}>
                <span className="group-id">{group.groupId}</span>
                <strong>{group.name}</strong>
                <span>{group.participants} 人</span>
                <span>{group.completed} 人完成</span>
              </div>
            ))}
          </div>
        </section>
        <section className="admin-section content-status">
          <div className="admin-section-heading"><div><h2>实验内容状态</h2><p>以下内容由当前三份材料生成。</p></div></div>
          <div className="source-row">
            {summary.content.sourceOfTruth.map((source) => <span key={source}>{source}</span>)}
          </div>
          <div className="id-lines">
            <p><strong>实验一：</strong>{summary.content.experimentOneTaskIds.join('、')}</p>
            <p><strong>实验二：</strong>{summary.content.experimentTwoTaskIds.join('、')}</p>
          </div>
          <div className="warning-list">
            {summary.content.warnings.map((warning) => <p key={warning}>{warning}</p>)}
          </div>
        </section>
        <section className="admin-section" id="participants">
          <div className="admin-section-heading"><div><h2>参与者记录</h2><p>显示登录账号、随机编号与实验进度。</p></div></div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>账号</th><th>编号</th><th>实验</th><th>分组</th><th>进度</th><th>状态</th><th>开始时间</th><th>完成码</th></tr></thead>
              <tbody>
                {participants.length === 0 ? (
                  <tr><td colSpan="8" className="empty-cell">暂无参与者数据</td></tr>
                ) : participants.map((person) => (
                  <tr key={person.id}>
                    <td>{person.username}</td>
                    <td>{person.participantCode}</td>
                    <td>{person.experimentId}</td>
                    <td>{person.groupId}</td>
                    <td>{person.completedTasks}/{person.totalTasks}</td>
                    <td><span className={`status-pill ${person.status}`}>{person.status === 'completed' ? '已完成' : '进行中'}</span></td>
                    <td>{new Date(person.startTime).toLocaleString('zh-CN')}</td>
                    <td>{person.completionCode || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="admin-section" id="exports">
          <div className="admin-section-heading"><div><h2>数据导出</h2><p>CSV 文件使用 UTF-8 编码，可直接用 Excel 打开。</p></div></div>
          <div className="export-grid">
            {EXPORTS.map(([name, label]) => (
              <button className="export-button" type="button" key={name} onClick={() => download(name)}>
                <span>{label}</span><small>{name}.csv</small>
              </button>
            ))}
          </div>
        </section>
        <section className="admin-section" id="settings">
          <div className="admin-section-heading"><div><h2>实验设置</h2><p>修改只影响之后新开始的任务。</p></div></div>
          <div className="settings-list">
            <SettingField label="每位参与者任务数" hint="默认从对应实验案例池随机抽取5题">
              <input type="number" min="1" max="8" value={settings.tasksPerParticipant} onChange={(event) => setSettings({ ...settings, tasksPerParticipant: Number(event.target.value) })} />
            </SettingField>
            <SettingField label="首次AI阅读倒计时" hint="材料出现后，等待多少秒可点击AI">
              <div className="number-unit"><input type="number" min="0" max="15" value={settings.aiReadDelaySeconds} onChange={(event) => setSettings({ ...settings, aiReadDelaySeconds: Number(event.target.value) })} /><span>秒</span></div>
            </SettingField>
            <SettingField label="候选回复阅读倒计时" hint="实验二直接生成组的候选回复锁定时间">
              <div className="number-unit"><input type="number" min="0" max="15" value={settings.candidateReadDelaySeconds} onChange={(event) => setSettings({ ...settings, candidateReadDelaySeconds: Number(event.target.value) })} /><span>秒</span></div>
            </SettingField>
            <SettingField label="随机排列候选回复" hint="减少推荐回复位置产生的顺序效应">
              <input className="toggle" type="checkbox" checked={settings.randomizeCandidateOrder} onChange={(event) => setSettings({ ...settings, randomizeCandidateOrder: event.target.checked })} />
            </SettingField>
            <SettingField label="实验一任务后问卷" hint="E1-CONF 与 E1-DIFF">
              <input className="toggle" type="checkbox" checked={settings.e1TaskSurveyEnabled} onChange={(event) => setSettings({ ...settings, e1TaskSurveyEnabled: event.target.checked })} />
            </SettingField>
            <SettingField label="实验二附加检查题" hint="MC2 与 HC2，当前默认不启用">
              <input className="toggle" type="checkbox" checked={settings.e2ControlChecksEnabled} onChange={(event) => setSettings({ ...settings, e2ControlChecksEnabled: event.target.checked })} />
            </SettingField>
            <SettingField label="实验二最终问卷" hint="正式题干尚未提供，因此保持停用">
              <input className="toggle" type="checkbox" checked={false} disabled />
            </SettingField>
          </div>
          <button className="button button-primary" type="button" onClick={saveSettings}>保存实验设置</button>
        </section>
      </main>
    </div>
  );
}

export default function AppV2() {
  const isAdmin = window.location.pathname.startsWith('/admin');
  const experimentId = useMemo(experimentFromLocation, []);
  const [account, setAccount] = useState(() => loadParticipantAccount());
  const [authReady, setAuthReady] = useState(isAdmin || !loadParticipantAccount());
  const accountId = account?.id;

  useEffect(() => {
    if (isAdmin || !accountId) {
      setAuthReady(true);
      return undefined;
    }
    let active = true;
    api.me()
      .then((response) => {
        if (!active) return;
        saveParticipantAccount(response.account);
        if (response.session) saveParticipantSession(response.session);
        else clearParticipantSession();
        setAccount(response.account);
      })
      .catch((requestError) => {
        if (!active) return;
        if (requestError.status === 401) {
          clearParticipantAuth();
          setAccount(null);
        }
      })
      .finally(() => {
        if (active) setAuthReady(true);
      });
    return () => { active = false; };
  }, [isAdmin, accountId]);

  function logout() {
    clearParticipantAuth();
    setAccount(null);
    if (window.location.pathname !== '/') window.location.href = '/';
  }

  if (isAdmin) return <AdminDashboard />;
  if (!authReady) return <LoadingScreen label="正在验证登录状态…" />;
  if (!account) return <ParticipantAuth onAuthenticated={setAccount} />;
  if (!experimentId) return <EntryChooser account={account} onLogout={logout} />;
  return <ParticipantApp experimentId={experimentId} account={account} />;
}
